import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarClock,
  CheckCircle2,
  Plus,
  RefreshCw,
  Sparkles,
  Users,
} from "lucide-react";
import {
  ChartType,
  ChartTypeMenu,
  useChartType,
} from "@/components/ChartTypeMenu";
import { OverviewChart } from "@/components/OverviewChart";
import { PlatformOverview } from "@/components/PlatformOverview";
import { Topbar } from "@/components/Topbar";
import { Button } from "@/components/Button";
import {
  dashboardService,
  DashboardOverview,
} from "@/services/dashboard.service";

const statuses = [
  { key: "active", label: "Active", color: "var(--color-success)" },
  { key: "trial", label: "Trial", color: "var(--color-primary)" },
  { key: "suspended", label: "Suspended", color: "var(--color-danger)" },
  { key: "expired", label: "Expired", color: "var(--color-text-muted)" },
];
const shortDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
const growthTypes: ChartType[] = ["bar", "line", "area", "pie", "donut"];
const healthTypes: ChartType[] = ["bar", "pie", "donut"];

export default function Dashboard() {
  const nav = useNavigate();
  const [growthType, setGrowthType] = useChartType(
    "growth",
    "bar",
    growthTypes,
  );
  const [healthType, setHealthType] = useChartType(
    "health",
    "donut",
    healthTypes,
  );
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await dashboardService.overview());
      setRefreshKey(value => value + 1);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const active = data?.byStatus.active ?? 0;
  const activePercent = data?.totalCompanies
    ? Math.round((active / data.totalCompanies) * 100)
    : 0;
  const statusData = statuses.map((s) => ({
    ...s,
    count: data?.byStatus[s.key] ?? 0,
  }));
  const signups = Array.from({ length: 30 }, (_, i) => {
    const day = new Date();
    day.setUTCHours(0, 0, 0, 0);
    day.setUTCDate(day.getUTCDate() - 29 + i);
    const date = day.toISOString().slice(0, 10);
    return {
      date,
      count: data?.signupsByDay.find((d) => d._id === date)?.count ?? 0,
    };
  });
  const totalSignups = signups.reduce((sum, day) => sum + day.count, 0);

  return (
    <>
      <Topbar
        title="Platform overview"
        breadcrumb="Overview / Dashboard"
        right={
          <Button
            aria-label="Refresh dashboard"
            variant="ghost"
            size="sm"
            loading={loading}
            onClick={load}
            iconLeft={<RefreshCw size={14} />}
          >
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        }
      />
      <div className="admin-page space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-text-primary">
              Your platform, at a glance
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              Company growth, account health and what needs your attention.
            </p>
          </div>
          <Button
            onClick={() => nav("/companies/new")}
            iconLeft={<Plus size={16} />}
          >
            Add company
          </Button>
        </div>
        {error && (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-xl border border-danger/20 bg-danger-soft p-4 text-sm text-danger"
          >
            <span>
              Could not refresh the overview.{" "}
              {data
                ? "The last loaded data is shown below."
                : "Please try again."}
            </span>
            <Button variant="secondary" size="sm" onClick={load}>
              Retry
            </Button>
          </div>
        )}
        {!data && loading ? (
          <div
            aria-label="Loading dashboard"
            className="grid grid-cols-2 gap-4 xl:grid-cols-4"
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="admin-panel h-32 animate-pulse bg-surface-alt"
              />
            ))}
          </div>
        ) : (
          data && (
            <>
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                {[
                  {
                    label: "Total companies",
                    value: data.totalCompanies,
                    detail: `${totalSignups} joined in the last 30 days`,
                    icon: Building2,
                    tone: "text-primary bg-primary-soft",
                  },
                  {
                    label: "Active companies",
                    value: active,
                    detail: `${activePercent}% of all companies`,
                    icon: CheckCircle2,
                    tone: "text-success bg-success-soft",
                  },
                  {
                    label: "Team members",
                    value: data.totalUsers,
                    detail: "Across all company accounts",
                    icon: Users,
                    tone: "text-info bg-info-soft",
                  },
                  {
                    label: "AI credits used",
                    value: data.aiCreditsUsedThisMonth,
                    detail: "Platform credits · this month",
                    icon: Sparkles,
                    tone: "text-primary bg-primary-soft",
                  },
                ].map((kpi) => (
                  <section key={kpi.label} className="admin-panel p-4 lg:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-xs font-medium text-text-secondary">
                        {kpi.label}
                      </h3>
                      <span className={`rounded-lg p-2 ${kpi.tone}`}>
                        <kpi.icon size={16} />
                      </span>
                    </div>
                    <p className="mt-1 text-3xl font-semibold tracking-tight text-text-primary tabular-nums">
                      {kpi.value.toLocaleString()}
                    </p>
                    <p className="mt-2 text-xs text-text-muted">{kpi.detail}</p>
                  </section>
                ))}
              </div>
              <PlatformOverview refreshKey={refreshKey} />
              <div className="grid gap-4 lg:grid-cols-5">
                <section className="admin-panel min-w-0 p-5 lg:col-span-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">Company growth</h3>
                      <p className="mt-1 text-xs text-text-muted">
                        New companies each day · last 30 days
                      </p>
                    </div>
                    <ChartTypeMenu
                      label="Company growth"
                      value={growthType}
                      onChange={setGrowthType}
                      allowed={growthTypes}
                    />
                  </div>
                  <div
                    className="mt-5 h-52"
                    role="img"
                    aria-label={`${totalSignups} new companies over the last 30 days`}
                  >
                    <OverviewChart
                      type={growthType}
                      data={signups.map((d) => ({
                        label: d.date,
                        value: d.count,
                      }))}
                      name="New companies"
                      formatLabel={shortDate}
                      center={
                        <>
                          <strong className="text-2xl font-semibold">
                            {totalSignups}
                          </strong>
                          <span className="text-xs text-text-muted">
                            new companies
                          </span>
                        </>
                      }
                    />
                  </div>
                  {totalSignups === 0 && (
                    <p className="mt-1 text-center text-xs text-text-muted">
                      No companies joined during this period.
                    </p>
                  )}
                </section>
                <section className="admin-panel p-5 lg:col-span-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold">Account health</h3>
                      <p className="mt-1 text-xs text-text-muted">
                        Companies by status
                      </p>
                    </div>
                    <ChartTypeMenu
                      label="Account health"
                      value={healthType}
                      onChange={setHealthType}
                      allowed={healthTypes}
                    />
                  </div>
                  <div
                    className="my-3 h-40"
                    role="img"
                    aria-label={statusData
                      .map((s) => `${s.label}: ${s.count}`)
                      .join(", ")}
                  >
                    <OverviewChart
                      type={healthType}
                      data={statusData.map((s) => ({
                        label: s.label,
                        value: s.count,
                        color: s.color,
                      }))}
                      name="Companies"
                      center={
                        <>
                          <strong className="text-2xl font-semibold tabular-nums">
                            {activePercent}%
                          </strong>
                          <span className="mt-0.5 text-[10px] uppercase tracking-wider text-text-muted">
                            Active
                          </span>
                        </>
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-x-5 gap-y-3">
                    {statusData.map((s) => (
                      <div
                        key={s.key}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: s.color }}
                        />
                        <span className="text-text-secondary">{s.label}</span>
                        <strong className="ml-auto tabular-nums">
                          {s.count}
                        </strong>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
              <div className="grid items-start gap-4 lg:grid-cols-5">
                <section className="admin-panel overflow-hidden lg:col-span-3">
                  <div className="flex items-center justify-between gap-3 p-5">
                    <div>
                      <h3 className="text-sm font-semibold">
                        Renewals & overdue accounts
                      </h3>
                      <p className="mt-1 text-xs text-text-muted">
                        Active and trial accounts due within 14 days
                      </p>
                    </div>
                    <CalendarClock size={18} className="text-primary" />
                  </div>
                  {data.expiringSoon.length === 0 ? (
                    <div className="flex items-center gap-3 border-t border-border px-5 py-7">
                      <CheckCircle2 size={22} className="text-success" />
                      <div>
                        <p className="text-sm font-medium">
                          No renewals need attention
                        </p>
                        <p className="mt-1 text-xs text-text-muted">
                          No accounts are overdue or due in the next 14 days.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="border-y border-border bg-surface-alt text-text-muted">
                          <tr>
                            <th className="px-5 py-2.5 font-medium">Company</th>
                            <th className="px-3 py-2.5 font-medium">
                              Expiry date
                            </th>
                            <th className="px-5 py-2.5 text-right font-medium">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.expiringSoon.map((c) => {
                            const days = Math.ceil(
                              (new Date(c.expiresAt).getTime() - Date.now()) /
                                864e5,
                            );
                            return (
                              <tr
                                key={c.companyUid}
                                className="border-b border-border last:border-0 hover:bg-surface-alt"
                              >
                                <td className="px-5 py-4">
                                  <Link
                                    className="font-semibold text-text-primary hover:text-primary focus-visible:underline"
                                    to={`/companies/${c.companyUid}`}
                                  >
                                    {c.name}
                                  </Link>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-text-secondary">
                                  {new Date(c.expiresAt).toLocaleDateString(
                                    undefined,
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  )}
                                </td>
                                <td className="px-5 py-4 text-right">
                                  <span
                                    className={`whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium ${days < 0 ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning"}`}
                                  >
                                    {days < 0
                                      ? "Overdue"
                                      : days === 0
                                        ? "Due today"
                                        : `${days} days left`}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Link
                    to="/companies"
                    className="flex items-center justify-center gap-2 border-t border-border py-3 text-xs font-semibold text-primary hover:bg-primary-soft"
                  >
                    View all companies <ArrowRight size={13} />
                  </Link>
                </section>
                <section className="admin-panel p-5 lg:col-span-2">
                  <h3 className="text-sm font-semibold">Quick actions</h3>
                  <p className="mt-1 text-xs text-text-muted">
                    Keep your platform moving
                  </p>
                  <div className="mt-4 space-y-2">
                    {[
                      {
                        to: "/companies/new",
                        title: "Onboard a company",
                        detail: "Create an account and assign a plan",
                        icon: Building2,
                      },
                      {
                        to: "/users",
                        title: "Manage team access",
                        detail: "Review users across your companies",
                        icon: Users,
                      },
                      {
                        to: "/ai-providers",
                        title: "Manage AI providers",
                        detail: "Configure models and platform access",
                        icon: Sparkles,
                      },
                    ].map((action) => (
                      <Link
                        key={action.to}
                        to={action.to}
                        className="group flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40 hover:bg-primary-soft"
                      >
                        <span className="rounded-lg bg-primary-soft p-2 text-primary">
                          <action.icon size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-semibold">
                            {action.title}
                          </span>
                          <span className="mt-1 block text-[11px] text-text-muted">
                            {action.detail}
                          </span>
                        </span>
                        <ArrowRight
                          size={14}
                          className="shrink-0 text-text-muted group-hover:text-primary"
                        />
                      </Link>
                    ))}
                  </div>
                </section>
              </div>
            </>
          )
        )}
      </div>
    </>
  );
}
